import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Lock, User, ArrowLeft, Eye, EyeOff, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "@/components/LoadingSpinner";

import { SecurityQuestionSetup } from "@/components/SecurityQuestionSetup";
import { PasswordResetForm } from "@/components/PasswordResetForm";
import { cleanupAuthState, signOutGlobally, reactivateUserIfNeeded, handleBookingContinuation } from "@/lib/auth-utils";
import { BackToHome } from "@/components/BackToHome";
import { Checkbox } from "@/components/ui/checkbox";
import { safeGetItem, safeRemoveItem } from "@/lib/safe-storage";
import { trackCompleteRegistration } from "@/lib/meta-pixel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const StudentAuth = () => {
  useEffect(() => {
    document.title = "Student Portal - Ascendry";
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showSecuritySetup, setShowSecuritySetup] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [newUserData, setNewUserData] = useState<any>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Sign In Form State
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  // Sign Up Form State
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");

  const SECURITY_QUESTIONS = [
    "What was the name of your first pet?",
    "What city were you born in?",
    "What was your mother's maiden name?",
    "What was the name of your first school?",
    "What was your childhood nickname?",
    "What street did you live on as a child?",
    "What was the make of your first car?",
    "What is your favorite movie?",
    "What was the name of your best friend in high school?",
    "What is your favorite food?"
  ];

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Clean up any existing auth state first
      cleanupAuthState();
      
      // Attempt global sign out first to prevent auth conflicts
      try {
        await signOutGlobally();
      } catch (err) {
        // Continue even if this fails
        console.warn('Global sign out failed during login:', err);
      }

      // Add a small delay for Safari to process the cleanup
      await new Promise(resolve => setTimeout(resolve, 100));

      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('ascendry_remember_me', 'true');
      } else {
        localStorage.removeItem('ascendry_remember_me');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: signInEmail,
        password: signInPassword,
      });

      if (error) throw error;

      if (data.user) {
        // Check if user is a student with retry logic
        let profile = null;
        let profileError = null;
        let attempts = 0;
        const maxAttempts = 3;

        while (attempts < maxAttempts && !profile) {
          attempts++;
          
          const profilePromise = supabase
            .from('profiles')
            .select('role, is_active')
            .eq('user_id', data.user.id)
            .single();

          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Profile fetch timeout')), 15000)
          );

          try {
            const result = await Promise.race([
              profilePromise,
              timeoutPromise
            ]) as any;
            
            profile = result.data;
            profileError = result.error;
          } catch (error) {
            profileError = error;
            if (attempts < maxAttempts) {
              // Wait 1 second before retrying
              await new Promise(resolve => setTimeout(resolve, 1000));
              console.log(`Profile fetch attempt ${attempts} failed, retrying...`);
            }
          }
        }

        if (profileError || !profile) {
          await supabase.auth.signOut();
          console.error('Profile fetch failed after retries:', profileError);
          throw new Error("Connection issue. Please check your internet and try again.");
        }

        if (profile?.role !== 'student') {
          await supabase.auth.signOut();
          throw new Error("This account is not registered as a student.");
        }

        // Reactivate account if it was deactivated
        const wasReactivated = await reactivateUserIfNeeded(data.user.id, profile.is_active);
        
        if (wasReactivated) {
          toast({
            title: "Account Reactivated!",
            description: "Welcome back! Your account has been reactivated.",
          });
        } else {
          toast({
            title: "Welcome back!",
            description: "You have been signed in successfully.",
          });
        }

        // Check for booking continuation
        const bookingData = handleBookingContinuation();
        if (bookingData) {
          toast({
            title: "Booking Continued",
            description: "We've restored your booking session. Complete your booking below.",
          });
        }

        // Check if user was trying to book a discovery call
        const pendingDiscoveryCall = safeGetItem('pendingDiscoveryCall');
        if (pendingDiscoveryCall) {
          // Redirect to mentor profile page (the page will auto-open the booking flow)
          navigate(`/mentor/${pendingDiscoveryCall}`);
        } else {
          // Navigate to student dashboard
          navigate('/dashboard/student');
        }
      }
    } catch (error: any) {
      toast({
        title: "Sign In Failed", 
        description: error.message || "Please check your credentials and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    console.log('🟢 FORM SUBMITTED - handleSignUp called');
    e.preventDefault();
    
    try {
      console.log('🟢 Starting validation checks...');
      
      // Validate security question
      if (!securityQuestion || !securityAnswer.trim()) {
        toast({
          title: "Security question required",
          description: "Please select a security question and provide an answer.",
          variant: "destructive",
        });
        return;
      }
      
      if (signUpPassword !== confirmPassword) {
        toast({
          title: "Error",
          description: "Passwords do not match.",
          variant: "destructive",
        });
        return;
      }

      if (signUpPassword.length < 6) {
        toast({
          title: "Error",
          description: "Password must be at least 6 characters long.",
          variant: "destructive",
        });
        return;
      }

      console.log('🟢 Validation passed, setting loading state...');
      setIsLoading(true);

      console.log('🟢 Calling supabase.auth.signUp...');
      const { data, error } = await supabase.auth.signUp({
        email: signUpEmail,
        password: signUpPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard/student`,
          data: {
            first_name: firstName,
            last_name: lastName,
            phone: phoneNumber,
            role: 'student'
          }
        }
      });
      
      console.log('🟢 Supabase signup response:', { data, error });

      if (error) {
        console.log('🔴 Signup error:', error);
        
        // Handle "user already exists" case
        if (error.message?.includes('already registered') || error.message?.includes('already exists')) {
          toast({
            title: "Account Already Exists",
            description: "This email is already registered. Please sign in instead.",
            variant: "destructive",
          });
          // Clear any session that might have been created
          await supabase.auth.signOut();
          return;
        }
        
        throw error;
      }

      if (data.user) {
        console.log('🟢 User created successfully');
        
        // Save security question
        const { error: securityError } = await supabase.functions.invoke('save-security-question', {
          body: {
            question: securityQuestion,
            answer: securityAnswer,
            user_id: data.user.id
          }
        });

        if (securityError) {
          console.error('🔴 Security question save error:', securityError);
          toast({
            title: "Warning",
            description: "Account created but security question failed to save. You can set it up later.",
            variant: "destructive",
          });
        } else {
          console.log('🟢 Security question saved successfully');
        }
        
        // If Supabase returns a session (email confirmation disabled), go straight to dashboard
        if (data.session?.user) {
          trackCompleteRegistration();
          toast({ title: "Account Created!", description: "Welcome to Ascendry." });
          navigate('/dashboard/student');
          return;
        }
        
        // Send admin notification using direct fetch (public endpoint)
        try {
          console.log('🟢 Sending admin notification for student signup...');
          const response = await fetch(
            'https://llcojpvgertoimsualob.supabase.co/functions/v1/send-admin-notification',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: 'student_signup',
                userEmail: signUpEmail,
                firstName: firstName,
                lastName: lastName
              })
            }
          );
          
          const responseData = await response.json();
          
          if (!response.ok) {
            console.error('🔴 Admin notification failed:', {
              status: response.status,
              statusText: response.statusText,
              data: responseData
            });
          } else {
            console.log('🟢 Admin notification sent successfully:', responseData);
          }
        } catch (notifError) {
          console.error('🔴 Admin notification request failed:', notifError);
          console.error('🔴 Error details:', {
            message: notifError?.message,
            stack: notifError?.stack
          });
          // Don't block signup if notification fails
        }
        
        // Track successful signup with Meta Pixel
        trackCompleteRegistration();
        
        toast({
          title: "Account Created!",
          description: "Signing you in...",
        });

        const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({
          email: signUpEmail,
          password: signUpPassword,
        });

        if (signInErr) {
          toast({ title: "Account created", description: "Please sign in to continue." });
        } else {
          navigate('/dashboard/student');
        }
      } else {
        console.log('🔴 No user data returned from signup');
        throw new Error("Account creation failed - no user data returned");
      }
    } catch (error: any) {
      console.error('🔴 Student signup error:', error);
      toast({
        title: "Error",
        description: error.message || "An error occurred during sign up.",
        variant: "destructive",
      });
    } finally {
      console.log('🟢 Setting isLoading to false');
      setIsLoading(false);
    }
  };

  const handleSecurityQuestionComplete = () => {
    setShowSecuritySetup(false);
    
    toast({
      title: "Security Question Set!",
      description: "Your account setup is complete. Welcome to Ascendry!",
    });

    // Navigate to student dashboard
    navigate('/dashboard/student');
  };

  const handleResetSuccess = () => {
    setShowResetForm(false);
    toast({
      title: "Password Reset Complete",
      description: "You can now sign in with your new password.",
    });
  };


  if (showSecuritySetup) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <SecurityQuestionSetup 
            userId={newUserData?.id}
            onComplete={handleSecurityQuestionComplete}
            title="Complete Your Registration"
          />
        </div>
      </div>
    );
  }


  if (showResetForm) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <Button 
            variant="ghost" 
            onClick={() => setShowResetForm(false)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </Button>
          <PasswordResetForm 
            onSuccess={handleResetSuccess}
            title="Reset Your Password"
            isForForgotPassword={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <img 
            src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
            alt="Ascendry" 
            className="h-12 w-auto mx-auto"
          />
          <h1 className="text-2xl font-bold text-energy">Student Portal</h1>
          <p className="text-muted-foreground">Access your learning journey</p>
        </div>

        <Card className="obsidian-card">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <CardHeader>
                <CardTitle className="text-energy">Welcome Back</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={signInEmail}
                        onChange={(e) => setSignInEmail(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="signin-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="remember-me"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                    />
                    <Label 
                      htmlFor="remember-me"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Remember me on this device
                    </Label>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing In..." : "Sign In"}
                  </Button>

                  <div className="text-center">
                    <Button 
                      type="button"
                      variant="link" 
                      className="text-sm"
                      onClick={() => setShowResetForm(true)}
                    >
                      Forgot your password?
                    </Button>
                  </div>
                </form>
              </CardContent>
            </TabsContent>

            <TabsContent value="signup">
              <CardHeader>
                <CardTitle className="text-energy">Create Account</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first-name">
                        First Name <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input
                          id="first-name"
                          type="text"
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="last-name">
                        Last Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="last-name"
                        type="text"
                        placeholder="Doe"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={signUpEmail}
                        onChange={(e) => setSignUpEmail(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone-number">
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="phone-number"
                        type="tel"
                        placeholder="ex: (420) 808-6969"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Choose a strong password"
                        value={signUpPassword}
                        onChange={(e) => setSignUpPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        minLength={6}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">
                      Confirm Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="confirm-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>

                  {/* Security Question Section */}
                  <div className="space-y-4 border-t pt-4">
                    <div className="text-center">
                      <h3 className="font-medium text-sm">Security Question</h3>
                      <p className="text-xs text-muted-foreground">For account recovery</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="security-question">Choose a security question</Label>
                      <Select value={securityQuestion} onValueChange={setSecurityQuestion} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a security question" />
                        </SelectTrigger>
                        <SelectContent>
                          {SECURITY_QUESTIONS.map((question, index) => (
                            <SelectItem key={index} value={question}>
                              {question}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="security-answer">Your answer</Label>
                      <Input
                        id="security-answer"
                        type="text"
                        placeholder="Enter your answer"
                        value={securityAnswer}
                        onChange={(e) => setSecurityAnswer(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                   
                   {/* Terms and Privacy Agreement */}
                   <div className="space-y-3 border-t pt-4">
                     <div className="flex items-start space-x-2">
                       <Checkbox 
                         id="terms-agreement" 
                         checked={acceptedTerms}
                         onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                         required
                       />
                       <label htmlFor="terms-agreement" className="text-sm leading-5">
                         I agree to the{" "}
                         <a 
                           href="/terms" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-primary hover:underline"
                         >
                           Terms of Use
                         </a>
                       </label>
                     </div>
                     
                     <div className="flex items-start space-x-2">
                       <Checkbox 
                         id="privacy-agreement" 
                         checked={acceptedPrivacy}
                         onCheckedChange={(checked) => setAcceptedPrivacy(checked as boolean)}
                         required
                       />
                       <label htmlFor="privacy-agreement" className="text-sm leading-5">
                         I acknowledge the{" "}
                         <a 
                           href="/privacy" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-primary hover:underline"
                         >
                           Privacy Policy
                         </a>
                       </label>
                     </div>
                    </div>

                    <Button
                     type="submit" 
                     className="w-full" 
                     disabled={isLoading || !acceptedTerms || !acceptedPrivacy}
                    onClick={(e) => {
                      console.log('🔴 BUTTON CLICKED - Direct onClick');
                      if (!isLoading) {
                        console.log('🔴 Button not disabled, form should submit');
                      } else {
                        console.log('🔴 Button is disabled, preventing submission');
                        e.preventDefault();
                      }
                    }}
                  >
                    {isLoading ? "Creating Account..." : "Create Account"}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        <div className="text-center">
          <BackToHome />
        </div>
      </div>
    </div>
  );
};

export default StudentAuth;